import { Features } from 'generated/featureDefinitions'
import { ShiftsFeatureFlagFragment } from 'generated/resources/graphql'

import type { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import { ShiftManagementFeatureFlagIds } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'

export interface EnabledFeaturesState {
  features: FeatureAvailabilityType[]
  featureFlags: ShiftsFeatureFlagFragment[]
  hasFeatureFlag: (featureName: ShiftManagementFeatureFlagIds) => boolean
  canUseYearlyBalance: boolean
  canUseCoverageView: boolean
  canUseCopySchedulePeriod: boolean
  canUseInsightsPanel: boolean
  canUseViewOptions: boolean
  canUseSaveShiftToggle: boolean
}

export type FeaturesDispatch = (arg0: FeaturesAction) => void

// Define actions if it's really needed
export type FeaturesAction = { type: '' }

const featureEnabled = (features: FeatureAvailabilityType[], feature: Features): boolean => {
  const featureAvailability = features.find((f) => f.id === feature)
  return featureAvailability?.available ?? false
}

const featureFlagEnabled = (
  featureFlags: ShiftsFeatureFlagFragment[],
  feature: ShiftManagementFeatureFlagIds
): boolean => {
  const featureFlag = featureFlags.find((f) => f.name === feature)
  return featureFlag?.enabled ?? false
}

const featuresAvailabilities = (
  features: FeatureAvailabilityType[],
  featureFlags: ShiftsFeatureFlagFragment[]
): EnabledFeaturesState => {
  const timeBalanceEnabled = isFeatureAvailabilityEnabled(
    features,
    Features.time_balance_annualisation_export
  )

  return {
    features,
    featureFlags,
    hasFeatureFlag: (featureName: ShiftManagementFeatureFlagIds) =>
      featureFlagEnabled(featureFlags, featureName),
    canUseYearlyBalance: timeBalanceEnabled,
    canUseCoverageView: featureEnabled(features, Features.shift_management_coverage_view),
    canUseCopySchedulePeriod: featureEnabled(
      features,
      Features.shift_management_copy_schedule_period
    ),
    canUseInsightsPanel: featureEnabled(features, Features.shift_management_insights_panel),
    canUseViewOptions: featureEnabled(features, Features.shift_management_view_options),
    canUseSaveShiftToggle: featureEnabled(features, Features.shift_management_save_shift_toggle),
  }
}

interface InitialStateProps {
  featureFlags: ShiftsFeatureFlagFragment[]
  features: FeatureAvailabilityType[]
}

export function initialEnabledFeaturesState({
  features,
  featureFlags,
}: InitialStateProps): EnabledFeaturesState {
  return featuresAvailabilities(features, featureFlags)
}

export default function reducer(
  state: EnabledFeaturesState,
  action: FeaturesAction
): EnabledFeaturesState {
  switch (action.type) {
    default:
      return state
  }
}
